import { apiFetch, ApiError } from '../api/client'

const STORAGE_KEY = 'pr-map-annexations-v1'

const readStored = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    return Array.isArray(value) ? value : []
  } catch {
    return []
  }
}

export const annexationStatusLabel = (status) => ({
  draft: 'Brouillon',
  submitted: 'Transmise au staff',
  changes_requested: 'Modifications demandées',
  approved: 'Approuvée',
  rejected: 'Refusée',
  withdrawn: 'Retirée',
}[status] || status || 'État inconnu')

export const loadStoredAnnexations = (countryId) => {
  if (!countryId) return []
  return readStored().filter((item) => Number(item.country_id) === Number(countryId))
}

export const rememberAnnexation = (request) => {
  const regionId = request?.payload?.region_id
  if (!request?.country_id || !regionId) return []
  // Keep only display data. The diplomatic argument remains server-side and
  // never enters durable browser storage shared by every local account.
  const stored = {
    id: request.id,
    country_id: request.country_id,
    status: request.status,
    submitted_at: request.submitted_at,
    payload: {
      region_id: regionId,
      region_name: request.payload.region_name,
    },
  }
  const others = readStored().filter((item) => (
    Number(item.country_id) !== Number(stored.country_id)
    || Number(item.payload?.region_id) !== Number(regionId)
  ))
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...others, stored]))
  return loadStoredAnnexations(stored.country_id)
}

export const submitAnnexation = async (regionId, reason) => {
  const response = await apiFetch('/map/my-annexation', {
    method: 'POST',
    credentials: 'include',
    // Sans cet en-tête le navigateur envoie `text/plain`, Flask rend `None`
    // pour le corps, et la route répond « Région invalide » : la demande
    // d'annexion depuis la carte n'aboutissait jamais. Le service et l'API
    // étaient pourtant corrects — seul le trajet navigateur manquait.
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ region_id: regionId, reason }),
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload?.success) {
    throw new ApiError(payload?.error || `HTTP ${response.status}`, { status: response.status })
  }
  return payload.data
}

/**
 * Ouvrir une chancellerie avec le pays qui habite une région.
 *
 * `credentials: 'include'` n'est pas optionnel : la route déduit l'appelant de
 * son cookie SSO, et sans lui elle répond 401. Deux fonctionnalités de cette
 * pile ont déjà été cassées cette semaine par ce seul oubli — les plans
 * militaires et le déploiement d'unités — chaque fois sans aucun message
 * d'erreur visible côté joueur.
 *
 * L'en-tête `Content-Type` ne l'est pas davantage : sans lui le navigateur
 * envoie `text/plain`, Flask rend `None` pour le corps, et la route répond
 * « Pays visé invalide » sur une requête pourtant correcte. C'est exactement ce
 * qui a empêché la demande d'annexion d'aboutir pendant des mois.
 */
export const openCountryChannel = async (countryId) => {
  const response = await apiFetch('/map/my-country-channel', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ country_id: countryId }),
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload?.success) {
    throw new ApiError(payload?.error || `HTTP ${response.status}`, { status: response.status })
  }
  return payload.data
}

/**
 * De quand date la carte, et combien de changements elle ignore.
 *
 * Le GeoJSON n'est pas une vue de la base : c'est un fichier régénéré par
 * `geojson-listener` à partir d'une file. Quand ce service s'arrête, rien ne le
 * dit — il l'a été cinq jours, avec 1843 changements en attente, pendant
 * lesquels un joueur qui fondait son pays ne le voyait apparaître nulle part.
 *
 * Sans `credentials` ni en-tête : la route est publique et ne révèle rien qu'un
 * visiteur ne puisse déduire en regardant la carte.
 */
export const fetchMapFreshness = async () => {
  const response = await apiFetch('/geojson/freshness', { timeout: 10000 })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload?.success) return null
  return payload.data
}
